// Canonical status-filter vocabulary for the deliverable Status chip.
// Single source of truth shared by the chip options (FilterChipBar) and
// the URL-state allow-list (deliverableUrlState) so the two can never
// drift and silently drop a status. A drift here previously left
// `conditionally-accepted` + `awaiting-resubmission` un-tickable: the
// chip offered them but the URL parse round-trip discarded the unknown
// tokens, so the checkbox never stayed checked.

import type { DeliverableStatus } from "../../../../types";

// Two derived overlay states sit alongside the stored DeliverableStatus
// enum on filter surfaces (see deriveDeliverableStatus): `overdue`
// (past due, not yet submitted) and `late` (submitted after due).
export type StatusFilterValue = DeliverableStatus | "overdue" | "late";

// Stored statuses keyed as a Record so adding/removing a DeliverableStatus
// is a compile error here until this list is updated — the stored half of
// the vocabulary can therefore never silently fall behind the type. Key
// order is preserved into STATUS_FILTER_VALUES below.
const STORED_STATUSES: Record<DeliverableStatus, true> = {
  "not-started": true,
  submitted: true,
  "under-review": true,
  "revision-requested": true,
  "conditionally-accepted": true,
  "awaiting-resubmission": true,
  accepted: true,
  rejected: true,
  closed: true,
};

export const STATUS_FILTER_VALUES: readonly StatusFilterValue[] = [
  ...(Object.keys(STORED_STATUSES) as DeliverableStatus[]),
  "overdue",
  "late",
];

// Display labels for the Status chip. A Record keyed by StatusFilterValue
// forces a label for every value, so a new status cannot reach the chip
// without a human-readable label.
export const STATUS_FILTER_LABELS: Record<StatusFilterValue, string> = {
  "not-started": "Not started",
  submitted: "Submitted",
  "under-review": "Under review",
  "revision-requested": "Revision requested",
  "conditionally-accepted": "Cond. accepted",
  "awaiting-resubmission": "Awaiting resub.",
  accepted: "Accepted",
  rejected: "Rejected",
  closed: "Closed",
  overdue: "Overdue",
  late: "Late",
};
